import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  setSignupData,
  setError,
  setVerificationStep,
} from "../../store/slices/signupSlice";
import { authService, searchService } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";

const PlayerDetailsStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { error, invitationContext } = useSelector((state) => state.signup);
  const [loading, setLoading] = useState(false);

  // Search states
  const [playerSearchQuery, setPlayerSearchQuery] = useState("");
  const [playerSearchResults, setPlayerSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const searchInputRef = useRef(null);
  const debouncedSearchQuery = useDebounce(playerSearchQuery, 300);

  // Search players when query changes
  useEffect(() => {
    if (debouncedSearchQuery.trim() && showSearchModal) {
      searchPlayers(debouncedSearchQuery, 1);
      setCurrentPage(1);
    } else if (!debouncedSearchQuery.trim()) {
      setPlayerSearchResults([]);
      setSearchPagination(null);
      setIsSearching(false);
    }
  }, [debouncedSearchQuery, showSearchModal]);

  const searchPlayers = async (query, page = 1) => {
    setIsSearching(true);
    try {
      const params = {
        type: "players",
        query: query,
        per_page: 10,
        page: page,
      };

      const response = await searchService.search(params);

      if (response.success && response.data) {
        // Updated to handle new response structure
        const players = response.data.results?.players || [];
        setPlayerSearchResults(players);
        setSearchPagination(response.pagination || null);
      } else {
        setPlayerSearchResults([]);
        setSearchPagination(null);
      }
    } catch (error) {
      console.error("Player search error:", error);
      setPlayerSearchResults([]);
      setSearchPagination(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePageChange = (page) => {
    if (playerSearchQuery.trim()) {
      setCurrentPage(page);
      searchPlayers(playerSearchQuery, page);
    }
  };

  const handlePlayerSelect = (player) => {
    setSelectedPlayer(player);
    setShowSearchModal(false);
    setPlayerSearchQuery("");
    setPlayerSearchResults([]);
    setSearchPagination(null);
  };

  const handleOpenSearch = () => {
    setShowSearchModal(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleConfirmSelection = async () => {
    if (!selectedPlayer) return;

    setLoading(true);
    dispatch(setError(null));

    try {
      // Create player with wyscout_id; include invite_token when present
      const playerData = {
        wyscout_id: parseInt(
          selectedPlayer.wyscout_id || selectedPlayer.id,
          10
        ),
      };

      if (invitationContext?.token) {
        playerData.invite_token = invitationContext.token;
      }

      console.log("Player creation payload:", playerData);
      const response = await authService.createPlayer(playerData);

      if (response.success) {
        dispatch(
          setSignupData({
            playerDetails: {
              wyscout_id: selectedPlayer.wyscout_id || selectedPlayer.id,
              selectedPlayer: selectedPlayer,
            },
          })
        );
        toast.success("Player profile created successfully!");
        navigate("/");
      } else {
        dispatch(setError(response.error || "Failed to create player profile"));
      }
    } catch (error) {
      console.error("Player creation error:", error);
      dispatch(
        setError("An unexpected error occurred while creating your profile")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    dispatch(setVerificationStep("type"));
  };

  return (
    <>
      <div className="flex items-center mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Back
        </button>
      </div>

      <div className="w-full">
        <div className="flex items-center justify-center w-12 h-12 bg-[#374DF5] rounded-lg mb-6 ">
          <svg
            width="18"
            height="19"
            viewBox="0 0 18 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.91732 4.08464C4.20898 4.08464 11.9173 4.91797 11.9173 7.0013C11.9173 8.46888 6.91732 12.8346 6.08398 13.2513C5.25065 13.668 4.00065 12.418 1.91732 10.7513L1.08398 12.418M10.2507 5.54297C7.584 7.87777 6.91791 9.08464 9.41783 10.3346C11.9177 11.5846 11.9173 12.0013 8.584 17.8346H10.2507M11.8596 6.69416C12.7507 8.66797 16.5007 8.66797 16.5007 6.16797M16.5007 16.1679C16.5006 17.0884 15.7544 17.8346 14.834 17.8346C13.9135 17.8347 13.1673 17.0885 13.1673 16.168C13.1673 15.2475 13.9135 14.5013 14.834 14.5013C15.7545 14.5013 16.5007 15.2475 16.5007 16.1679ZM14.834 2.8346C14.834 3.75507 14.0878 4.50128 13.1673 4.5013C12.2468 4.50132 11.5006 3.75514 11.5007 2.83467C11.5007 1.9142 12.2469 1.16799 13.1674 1.16797C14.0878 1.16795 14.834 1.91413 14.834 2.8346Z"
              stroke="white"
              stroke-width="1.27075"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <h2 className="text-2xl leading-[28px] font-bold text-gray-900 mb-2">
          Setup Player
        </h2>
        <p className="text-[14px] leading-[20px] text-[#6C737F] mb-8">
          Search for your player profile to complete registration
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Player Selection */}
        <div className="space-y-3">
          <label className="block text-base font-medium text-[#000000] mb-2">
            Search your profile
          </label>

          <div className="border border-gray-300 rounded-lg h-12 flex items-center">
            {selectedPlayer ? (
              <div className="flex items-center justify-between w-full px-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full bg-gray-300 bg-cover bg-center"
                    style={{
                      backgroundImage: selectedPlayer.image_path
                        ? `url(${selectedPlayer.image_path})`
                        : "none",
                    }}
                  />
                  <span className="text-base text-gray-900 font-medium">
                    {selectedPlayer.display_name || selectedPlayer.name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPlayer(null);
                    handleOpenSearch();
                  }}
                  className="text-secondary hover:text-gray-900"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleOpenSearch}
                className="w-full h-full px-4 text-left"
              >
                <span className="text-base text-gray-500">
                  Enter your name to search...
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Player Search Modal */}
        {showSearchModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-semibold text-[#202630]">
                  Search Player Profiles
                </h2>
                <button
                  type="button"
                  onClick={() => setShowSearchModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Search Input */}
              <div className="p-6 border-b">
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Enter your name to search..."
                    value={playerSearchQuery}
                    onChange={(e) => setPlayerSearchQuery(e.target.value)}
                    className="w-full h-12 pl-12 pr-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg
                    className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {isSearching && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                    </div>
                  )}
                </div>
              </div>

              {/* Search Results */}
              <div className="flex-1 overflow-y-auto p-6">
                {isSearching && playerSearchResults.length === 0 ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                      <p className="text-secondary">Searching players...</p>
                    </div>
                  </div>
                ) : playerSearchResults.length > 0 ? (
                  <div className="space-y-4">
                    {/* Results List */}
                    <div className="space-y-2">
                      {playerSearchResults.map((player) => (
                        <button
                          key={player.id}
                          type="button"
                          onClick={() => handlePlayerSelect(player)}
                          className="w-full p-4 border border-[#E9E9E9] rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0"
                              style={{
                                backgroundImage:
                                  player.image_path &&
                                  !player.image_path.includes("placeholder")
                                    ? `url(${player.image_path})`
                                    : "none",
                              }}
                            />

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h3 className="text-base font-medium text-[#1A212F] truncate">
                                  {player.display_name || player.name}
                                </h3>
                                {player.date_of_birth && (
                                  <span className="text-sm text-secondary flex-shrink-0">
                                    {Math.floor(
                                      (new Date() -
                                        new Date(player.date_of_birth)) /
                                        (365.25 * 24 * 60 * 60 * 1000)
                                    )}{" "}
                                    years
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="truncate">
                                  {player.firstname} {player.lastname}
                                </span>

                                {player.position && (
                                  <span className="flex-shrink-0">
                                    {player.position}
                                  </span>
                                )}

                                {player.country && (
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {player.country.image_path && (
                                      <img
                                        src={player.country.image_path}
                                        alt={player.country.name}
                                        className="w-4 h-3 object-cover rounded-sm"
                                      />
                                    )}
                                    <span>{player.country.name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Pagination */}
                    {searchPagination && (
                      <div className="pt-4 border-t">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-secondary">
                            Showing {playerSearchResults.length} results
                          </div>

                          {(searchPagination.total_pages > 1 ||
                            searchPagination.has_more) && (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage <= 1}
                                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                              >
                                Previous
                              </button>

                              <span className="px-2 text-sm text-secondary">
                                {currentPage}
                              </span>

                              <button
                                type="button"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={
                                  !searchPagination.has_more &&
                                  currentPage >= searchPagination.total_pages
                                }
                                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : playerSearchQuery.trim() && !isSearching ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <svg
                        className="w-12 h-12 text-gray-300 mx-auto mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <p className="text-secondary">
                        No players found for "{playerSearchQuery}"
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        Try adjusting your search terms
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <svg
                        className="w-12 h-12 text-gray-300 mx-auto mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <p className="text-secondary">
                        Start typing to search for your player profile
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        Enter your name to find your profile in our database
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Button */}
        {selectedPlayer && (
          <div className="mt-6">
            <button
              onClick={handleConfirmSelection}
              disabled={loading}
              className={`w-full py-3 px-4 rounded-lg transition-colors ${
                loading
                  ? "bg-blue-400 text-[#F5F5F5] cursor-not-allowed"
                  : "bg-[#2283FF] hover:bg-[#1a6cd4] text-[#F5F5F5]"
              }`}
            >
              {loading ? "Creating Profile..." : "Save & Continue"}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default PlayerDetailsStep;
